import { useMemo } from 'react';
import { Flex, Icon, IconButton, Input, Text } from '@chakra-ui/react';
import { EyeIcon, TrashIcon } from '@heroicons/react/24/outline';
import { PlanItem } from '@/lib/types';
import { slotFor } from '@/lib/slot-color';
import { SortableList } from '@/components/sortable-list';
import { SimpleSelect } from '@/components/ui/simple-select';
import useContentTypesStore from '@/utils/use-content-type';

interface ItemRowProps {
  item: PlanItem;
  onChange: (item: PlanItem) => void;
  onRemove: () => void;
  onViewDetail?: () => void;
  // Highlights the library select on custom items that still lack one —
  // flipped on by builder-screen when a save is attempted with missing labels.
  showRequiredError?: boolean;
}

// One content item within a section (rendered inside a nested SortableList —
// drag from the handle only). Linked items (content_id set) show a name +
// type badge; custom items show a name Input + a required content-library
// select — the chosen library makes the item materialize into a draft content
// of that library on plan save (see builder-screen #handleSave). Eye icon
// opens the content-item detail slide-over.
export default function ItemRow({ item, onChange, onRemove, onViewDetail, showRequiredError }: ItemRowProps) {
  // Populated by the navbar on workspace mount (navigation-menu.tsx) — same
  // read-only reliance as calendar/event-modal.tsx.
  const contentTypes = useContentTypesStore((s) => s.contentTypes);
  const isLinked = item.content_id != null;
  const slot = isLinked ? slotFor(String(item.content_type_id)) : null;

  const libraryItems = useMemo(
    () => contentTypes.map((t) => ({ label: t.name, value: String(t.id) })),
    [contentTypes],
  );

  const handleLibraryChange = (value: string) => {
    const selected = contentTypes.find((t) => String(t.id) === value);
    onChange({
      ...item,
      content_type_id: selected?.id ?? null,
      content_type_name: selected?.name ?? null,
      // The library name supersedes any legacy free-text label.
      custom_label: selected ? null : item.custom_label,
    });
  };

  return (
    <Flex align='center' gap={2} py={1.5}>
      <SortableList.DragHandle />
      {isLinked ? (
        <>
          <Text fontSize='sm' color='fg.strong' flex='1' lineClamp={1}>
            {item.name}
          </Text>
          {item.content_type_name && (
            <Text
              as='span'
              fontSize='xs'
              fontWeight='semibold'
              px={2}
              py={0.5}
              borderRadius='full'
              bg={`slot${slot}.bg`}
              color={`slot${slot}.fg`}
              flexShrink={0}
            >
              {item.content_type_name}
            </Text>
          )}
        </>
      ) : (
        <>
          <Input
            value={item.name}
            onChange={(e) => onChange({ ...item, name: e.target.value })}
            placeholder='Custom item name'
            size='sm'
            flex='1'
          />
          <SimpleSelect
            items={libraryItems}
            size='sm'
            maxW='36'
            flexShrink={0}
            value={item.content_type_id != null ? String(item.content_type_id) : ''}
            onValueChange={handleLibraryChange}
            placeholder='Select label'
            // The DS border lives on the trigger slot, which SimpleSelect
            // doesn't expose directly — reach it via Ark's data-part.
            css={
              showRequiredError && item.content_type_id == null
                ? { '& [data-part=trigger]': { borderColor: 'danger' } }
                : undefined
            }
          />
        </>
      )}

      <IconButton
        aria-label='View detail'
        variant='ghost'
        size='xs'
        onClick={onViewDetail}
        disabled={!onViewDetail}
      >
        <Icon as={EyeIcon} w={4} h={4} />
      </IconButton>
      <IconButton
        aria-label='Remove item'
        variant='ghost'
        size='xs'
        onClick={onRemove}
        _hover={{ color: 'danger', bg: 'danger.subtle' }}
      >
        <Icon as={TrashIcon} w={4} h={4} />
      </IconButton>
    </Flex>
  );
}
